<template>
    <div class="panel-body">
        <div class="panel-header">
            <span class="panel-title">{{ $tc('common.messages', 2) }}</span>
            <span v-if="messagesCount > 0" class="panel-badge">{{ messagesCount }}</span>
        </div>

        <select-option-students class="panel-tabs" underline :custom-options="groupOptions" :value="group"
            @change="group = $event" />

        <div class="panel-list">
            <chats component-mode :search-chat="search" :group-filter="group" :coach-ids="coachIds" ref="chats" />
        </div>

        <div class="panel-footer">
            <kimsa-text-field class="panel-search" @change="search = $event" :value="search"
                :placeholder="$t('common.search')" dense />
            <button-tooltip top :tooltip="$t('common.refresh')">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="panel-btn" v-bind="attrs" v-on="on" @click="reload">
                        <v-icon size="16">mdi-refresh</v-icon>
                    </button>
                </template>
            </button-tooltip>
            <button-tooltip top :tooltip="$t('tooltips.add_chat')">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="panel-btn accent-btn" v-bind="attrs" v-on="on" @click="showAddChat">
                        <v-icon size="16">mdi-plus</v-icon>
                    </button>
                </template>
            </button-tooltip>
        </div>

        <create-company-chat-dialog ref="createChat" @success="onCreatedChat" :coaches="coaches" />
    </div>
</template>

<script>
import { UserIndex } from "@/utils/Algolia";
import Chats from "@/views/company/Chats";
import SelectOptionStudents from "@/components/coach/SelectOptionStudents";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import CreateCompanyChatDialog from "@/components/company/CreateCompanyChatDialog";

export default {
    name: "CompanyAdminPanelBody",
    components: { Chats, SelectOptionStudents, KimsaTextField, ButtonTooltip, CreateCompanyChatDialog },
    data(vm = this) {
        return {
            group: 'student',
            search: '',
            coaches: [],
            groupOptions: [
                { name: vm.$t('common.students'), value: 'student' },
                { name: vm.$t('common.coaches'), value: 'coach' },
            ],
        }
    },
    computed: {
        companyId(vm = this) {
            return vm.currentUser?.uid || null
        },
        coachIds(vm = this) {
            return vm.coaches.map(coach => coach.id)
        },
        messagesCount(vm = this) {
            return Math.max(0, Number(vm.account?.counter?.messages) || 0)
        },
    },
    mounted() {
        this.loadCoaches()
    },
    methods: {
        async loadCoaches() {
            const vm = this
            if (!vm.companyId) return
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `coachSubscription.companyId: ${vm.companyId}`,
                    hitsPerPage: 1000,
                })
                vm.coaches = hits.map(hit => ({
                    ...hit,
                    id: hit.objectID,
                    name: hit.name || hit.email || vm._noAvailableName,
                }))
            } catch (error) {
                console.log('loadCoaches error', error)
            }
        },
        reload() {
            const vm = this
            if (vm.$refs.chats) vm.$refs.chats.loadChats()
        },
        showAddChat() {
            this.$refs.createChat.show()
        },
        onCreatedChat(payload) {
            const vm = this
            if (vm.$refs.chats) vm.$refs.chats.openChat(payload)
        },
    },
}
</script>

<style scoped>
.panel-body {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.panel-header {
    height: 60px;
    border-bottom: 1px solid var(--v-outline-base);
    padding: 0 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.panel-badge {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-primary-base);
    background: rgba(204, 255, 51, 0.12);
    border: 1px solid rgba(204, 255, 51, 0.22);
    border-radius: 10px;
    padding: 2px 8px;
}

.panel-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.panel-tabs {
    padding: 0 12px;
    flex-shrink: 0;
}

.panel-list {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding: 8px 12px;
}

.panel-list::-webkit-scrollbar {
    width: 5px;
}

.panel-list::-webkit-scrollbar-track {
    background: transparent;
}

.panel-list::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 3px;
}

.panel-footer {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 12px calc(10px + var(--safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.panel-search {
    flex: 1 1 auto;
    min-width: 0;
}

.panel-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    transition: color 0.15s, border-color 0.15s;
}

.panel-btn:hover {
    color: var(--v-txtPrimary-base);
}

.panel-btn.accent-btn {
    color: var(--v-accent-base);
    border-color: rgba(51, 153, 153, 0.35);
}
</style>
